/**
 * MissionTrace — Timeline temps réel des traces d'une mission
 * Connecté via SSE (EventSource) pour les updates live
 */

import { useRef, useEffect } from "react";
import type { Trace } from "../hooks/useMissionTraces";

const ACTION_TYPE_LABELS: Record<string, string> = {
  code_write: "Code",
  search: "Recherche",
  test_run: "Test",
  question: "Question",
  decision: "Décision",
};

const STATUS_ICON: Record<string, string> = {
  success: "✅",
  error: "❌",
  running: "⏳",
};

export default function MissionTrace({
  traces,
  connected,
}: {
  traces: Trace[];
  connected: boolean;
}) {
  const feedRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new traces
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [traces]);

  return (
    <div className="timeline-panel">
      <div className="timeline-header">
        <strong style={{ fontSize: "16px" }}>📡 Trace d'exécution</strong>
        <div className="timeline-tabs">
          <span className={`timeline-tab active`}>
            {connected ? "🟢 Live" : "🔴 Hors ligne"}
          </span>
        </div>
      </div>

      <div className="trace-feed" ref={feedRef}>
        {traces.length === 0 && (
          <div className="no-results" style={{ padding: "40px" }}>
            Aucune trace. Démarre la mission pour voir l'exécution en temps réel.
          </div>
        )}

        {traces.map((trace) => (
          <div key={trace.id} className="trace-item">
            <div className="trace-spine">
              <div className="trace-spine-line" />
              <div className={`trace-spine-dot ${trace.status}`}>
                {STATUS_ICON[trace.status]}
              </div>
            </div>
            <div className="trace-content">
              <div className="trace-agent-row">
                <span className="trace-agent-name">{trace.agentName}</span>
                {trace.agentRole && (
                  <span className="trace-agent-role">{trace.agentRole}</span>
                )}
                <span className={`trace-action-type ${trace.actionType}`}>
                  {ACTION_TYPE_LABELS[trace.actionType] || trace.actionType}
                </span>
                <span className="trace-time">
                  {new Date(trace.createdAt).toLocaleTimeString("fr-FR")}
                </span>
                {trace.durationMs !== null && trace.durationMs !== undefined && (
                  <span className="trace-time">{(trace.durationMs / 1000).toFixed(1)}s</span>
                )}
              </div>
              <div className="trace-desc">{trace.description}</div>
              {trace.status === "error" && trace.errorMessage && (
                <div className="trace-error-box">{trace.errorMessage}</div>
              )}
              {trace.artifacts.length > 0 && (
                <div className="trace-artifacts">
                  {trace.artifacts.map((a, i) => (
                    <span key={i} className="artifact-chip">📎 {a.slice(0, 60)}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
